import React, { useState } from 'react';
import { Mail, Key, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Email, 2: Código, 3: Nova Senha
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ============================================
  // PASSO 1: Solicitar código
  // ============================================
  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSuccess('Código enviado para seu email!');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao solicitar código');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PASSO 2: Verificar código
  // ============================================
  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await axios.post(`${API}/api/auth/verify-reset-code`, { 
        email, 
        code 
      });
      setResetToken(res.data.reset_token);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.error || 'Código inválido');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PASSO 3: Redefinir senha
  // ============================================
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        reset_token: resetToken,
        new_password: newPassword
      });
      setSuccess('Senha redefinida com sucesso! Redirecionando...');
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Key size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Recuperação de Senha</h1>
          <p className="text-gray-500 text-sm">
            {step === 1 && 'Digite seu email para receber o código'}
            {step === 2 && 'Digite o código recebido no email'}
            {step === 3 && 'Crie uma nova senha'}
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm ${
                step >= s ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s}
              </div>
              {s < 3 && (
                <div className={`w-16 h-1 ${step > s ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Email */}
        {step === 1 && (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Email cadastrado
              </label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)} 
                  placeholder="seu@email.com"
                  required
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none" 
                />
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-xl text-sm text-red-700 flex items-start gap-2">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-50 border-l-4 border-green-500 rounded-xl text-sm text-green-700 flex items-start gap-2">
                <CheckCircle2 size={18} className="flex-shrink-0 mt-0.5" />
                <span>{success}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-red-700 disabled:opacity-50">
              {loading ? 'Enviando...' : 'Enviar Código'}
            </button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-800 flex items-center justify-center gap-1">
                <ArrowLeft size={14} /> Voltar para o login
              </Link>
            </div>
          </form>
        )}

        {/* Step 2: Código */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Código de verificação
              </label>
              <input 
                type="text" 
                value={code} 
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} 
                placeholder="000000"
                maxLength={6}
                required
                className="w-full p-4 text-center text-3xl font-mono font-bold tracking-widest border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 outline-none" 
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                Digite os 6 dígitos enviados para {email}
              </p>
            </div>

            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-xl text-sm text-red-700 flex items-start gap-2">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-xl font-bold hover:from-orange-600 hover:to-red-700 disabled:opacity-50">
              {loading ? 'Verificando...' : 'Verificar Código'}
            </button>

            <button 
              type="button"
              onClick={() => setStep(1)}
              className="w-full py-2 text-sm text-gray-600 hover:text-gray-800">
              ← Voltar para email
            </button>
          </form>
        )}

        {/* Step 3: Nova Senha */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nova senha
              </label>
              <input 
                type="password" 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                required
                className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none" 
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirmar senha
              </label>
              <input 
                type="password" 
                value={confirmPassword} 
                onChange={e => setConfirmPassword(e.target.value)} 
                placeholder="Digite a senha novamente"
                required
                className="w-full p-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none" 
              />
            </div>

            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-xl text-sm text-red-700 flex items-start gap-2">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button 
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:from-green-600 hover:to-green-700 disabled:opacity-50">
              {loading ? 'Redefinindo...' : 'Redefinir Senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;